import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BaseWebPortalService,
  DepartmentCode,
  PortalId,
  WebPortal,
} from '@gk/gk-modules';
import {
  defaultIfEmpty,
  filter,
  find,
  map,
  mergeMap,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { MainRoutes } from '../../guards/guard.models';
import {
  MainWebPortalUrl,
  WebPortAlType,
} from '../../modules/office-departments-portal/departments/departments.model';
import { PortalSettingsAccess } from './web-portal.model';

@Injectable()
export class WebPortalService extends BaseWebPortalService {
  private mainWebPortalData: Observable<WebPortal>;
  private webPortalsAvailableForGrantingRights: Observable<WebPortal[]>;

  constructor(public override httpClient: HttpClient) {
    super(httpClient);
  }

  getWebPortalDataByCurrentRoute(
    portalRoute: MainRoutes,
  ): Observable<WebPortal> {
    return this.getWebPortalData().pipe(
      mergeMap((data) => data),
      filter((webPortalData) => {
        const url = webPortalData.url;
        const pattern = new RegExp(`(^|/)${portalRoute}($|/)`);

        return url && pattern.test(url);
      }),
      defaultIfEmpty(undefined),
    );
  }

  getMainWebPortalData(): Observable<WebPortal> {
    if (!this.mainWebPortalData) {
      this.mainWebPortalData = this.getWebPortalData().pipe(
        mergeMap((data) => data),
        find((webPortalData) => webPortalData.url === MainWebPortalUrl),
        shareReplay(1),
      );
    }

    return this.mainWebPortalData;
  }

  isPortalAvailableForGrantingRights(portalId: PortalId): Observable<boolean> {
    return this.isGrantingRightsPortalAvailable().pipe(
      switchMap((isGrantingRightsPortalAvailable) => {
        if (!isGrantingRightsPortalAvailable) {
          return of(false);
        }

        return this.getWebPortalsAvailableForGrantingRights().pipe(
          map((webPortals) => {
            const webPortal = webPortals.find(
              (webPortal) => webPortal.portalId === portalId,
            );
            return !!webPortal;
          }),
        );
      }),
    );
  }

  isGrantingRightsPortalAvailable(): Observable<boolean> {
    return this.getWebPortalData().pipe(
      map((webPortalData) => {
        const webPortal = webPortalData.find(
          (webPortal) => webPortal.portalId === PortalId.GrantingRights,
        );
        return !!webPortal;
      }),
    );
  }

  getWebPortalsAvailableForGrantingRights(): Observable<WebPortal[]> {
    if (!this.webPortalsAvailableForGrantingRights) {
      this.webPortalsAvailableForGrantingRights = this.getWebPortalData().pipe(
        map((data) => data.filter((webPortal) => webPortal.eRequest)),
        shareReplay(1),
      );
    }
    return this.webPortalsAvailableForGrantingRights;
  }

  getPortalSettingsAccess(): Observable<PortalSettingsAccess> {
    return this.getWebPortalData().pipe(
      map((webPortalData) =>
        PortalSettingsAccess.getPortalSettingsAccess(webPortalData),
      ),
    );
  }

  getGeodesyWebPortals(): Observable<WebPortal[]> {
    return this.getWebPortalData().pipe(
      map((data) =>
        data.filter(
          (webPortal) =>
            webPortal.departmentCode === DepartmentCode.Geodesy &&
            webPortal.portalId !== PortalId.IntraEwid &&
            !webPortal.masterId,
        ),
      ),
    );
  }

  getSpatialInformationWebPortals(): Observable<WebPortal[]> {
    return this.getWebPortalData().pipe(
      map((data) =>
        data.filter(
          (webPortal) =>
            webPortal.type === WebPortAlType.External &&
            webPortal.portalId >= 100,
        ),
      ),
    );
  }
}
